import { cn } from "@d12labs/utils";
import type { CSSProperties, ReactNode } from "react";

/**
 * Linear-dense list/table. 34px rows, hairline dividers, hover lifts onto
 * surface-1. The same column definitions drive the header and every row so the
 * grid tracks line up. This replaces the bespoke `.map()` row markup each plugin
 * used to roll by hand.
 *
 * Column `width` is any CSS grid track ("1fr", "120px", "1.4fr"); default "1fr".
 */
export type ListColumn = { width?: string; align?: "left" | "right" };
export type ListHeaderCol = ListColumn & { label?: ReactNode };
export type ListCell = ListColumn & { value: ReactNode; muted?: boolean; mono?: boolean };

/** Grid-template-columns from a set of column defs. */
export function listGridStyle(cols: ListColumn[]): CSSProperties {
	return { gridTemplateColumns: cols.map((c) => c.width ?? "1fr").join(" ") };
}

/** Shared row layout — also used by `SkeletonRow` and interactive (Link) rows. */
export const listRowBase = "grid items-center gap-3 px-6 transition-colors";

/** Optional bordered container that frames a list as a surface-1 card. */
export function List({ className, children }: { className?: string; children: ReactNode }) {
	return (
		<div
			data-slot="list"
			className={cn(
				"overflow-hidden rounded-lg border border-hairline bg-surface-1",
				// The framing card border already closes the list — drop the last row's divider.
				"[&>*:last-child]:border-b-0",
				className,
			)}
		>
			{children}
		</div>
	);
}

export function ListHeader({
	columns,
	className,
}: {
	columns: ListHeaderCol[];
	className?: string;
}) {
	return (
		<div
			data-slot="list-header"
			className={cn(
				listRowBase,
				"h-9 border-b border-hairline bg-canvas text-eyebrow uppercase text-ink-tertiary",
				className,
			)}
			style={listGridStyle(columns)}
		>
			{columns.map((c, i) => (
				<div
					// biome-ignore lint/suspicious/noArrayIndexKey: static column config, never reordered
					key={i}
					className={cn("min-w-0 truncate", c.align === "right" && "text-right")}
				>
					{c.label}
				</div>
			))}
		</div>
	);
}

export function ListRow({
	cells,
	selected = false,
	interactive = true,
	className,
	render,
}: {
	cells: ListCell[];
	/** Highlight as the active/selected row (surface-2 fill). */
	selected?: boolean;
	/** Apply the hover lift. Set false for non-clickable rows. */
	interactive?: boolean;
	className?: string;
	/**
	 * Wrap the row in a custom element (e.g. a router `<Link>` or `<button>`).
	 * Receives the computed className/style/children — spread them onto your node.
	 */
	render?: (props: { className: string; style: CSSProperties; children: ReactNode }) => ReactNode;
}) {
	const rowClassName = cn(
		listRowBase,
		"h-11 border-b border-hairline text-sm",
		selected ? "bg-surface-2 text-foreground" : interactive && "hover:bg-surface-1",
		className,
	);
	const style = listGridStyle(cells);
	const children = cells.map((c, i) => (
		<div
			// biome-ignore lint/suspicious/noArrayIndexKey: cells are positional, keyed by column index
			key={i}
			className={cn(
				"min-w-0 truncate",
				c.muted ? "text-ink-subtle" : "text-foreground",
				c.align === "right" && "text-right tabular-nums",
				c.mono && "mono-recipe text-caption",
			)}
		>
			{c.value}
		</div>
	));

	if (render) return <>{render({ className: rowClassName, style, children })}</>;
	return (
		<div data-slot="list-row" className={rowClassName} style={style}>
			{children}
		</div>
	);
}
